import React, { useEffect, useRef, useState } from "react";
import { Building2, Upload, Loader2, ImageIcon, User, KeyRound, Sparkles, Landmark } from "lucide-react";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { fileUrl, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TeamSettings from "@/components/TeamSettings";
import CardPayments from "@/components/CardPayments";
import XeroConnect from "@/components/XeroConnect";
import ActivityLog from "@/components/ActivityLog";

const FIELDS = [
  { key: "company_name", label: "Company name", placeholder: "e.g. CT Builders Ltd" },
  { key: "email", label: "Email", placeholder: "info@yourcompany.co.uk" },
  { key: "phone", label: "Phone", placeholder: "0113 555 0100" },
  { key: "website", label: "Website", placeholder: "www.yourcompany.co.uk" },
  { key: "vat_number", label: "VAT number", placeholder: "GB123456789" },
  { key: "company_number", label: "Companies House no.", placeholder: "12345678" },
  { key: "cis_utr", label: "CIS registration / UTR", placeholder: "10-digit UTR" },
];

const PAYMENT_FIELDS = [
  { key: "account_name", label: "Account name", placeholder: "e.g. CT Builders Ltd" },
  { key: "bank_name", label: "Bank", placeholder: "e.g. Barclays" },
  { key: "sort_code", label: "Sort code", placeholder: "12-34-56" },
  { key: "account_number", label: "Account number", placeholder: "12345678" },
  { key: "payment_reference", label: "Payment reference", placeholder: "e.g. project name or invoice no." },
];

const Settings = () => {
  const { user, tenant, refreshMe } = useAuth();
  const [branding, setBranding] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [profile, setProfile] = useState({ name: "", company: "" });
  const [savingProfile, setSavingProfile] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);
  const [aiUsage, setAiUsage] = useState(null);
  const [payment, setPayment] = useState({});
  const [savingPayment, setSavingPayment] = useState(false);

  useEffect(() => {
    api.get("/branding").then(({ data }) => setBranding(data || {})).catch(() => {});
    api.get("/payment-settings").then(({ data }) => setPayment(data || {})).catch(() => {});
    api.get("/ai/usage").then(({ data }) => setAiUsage(data)).catch(() => {});
  }, []);
  useEffect(() => {
    setProfile({ name: user?.name || "", company: tenant?.company_name || "" });
  }, [user, tenant]);

  const set = (k, v) => setBranding((b) => ({ ...b, [k]: v }));
  const setPay = (k, v) => setPayment((p) => ({ ...p, [k]: v }));

  const savePayment = async () => {
    setSavingPayment(true);
    try {
      const { account_name, bank_name, sort_code, account_number, payment_reference, payment_terms } = payment;
      const { data } = await api.put("/payment-settings", { account_name, bank_name, sort_code, account_number, payment_reference, payment_terms });
      setPayment((p) => ({ ...p, ...data }));
      toast.success("Payment details saved — they'll show on your Applications for Payment");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingPayment(false); }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await api.put("/auth/profile", { name: profile.name, company: profile.company });
      await refreshMe();
      toast.success("Profile updated");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    if (pw.new_password !== pw.confirm) { toast.error("New passwords don't match"); return; }
    setSavingPw(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: pw.current_password, new_password: pw.new_password });
      if (data.token) localStorage.setItem("bs_token", data.token);
      setPw({ current_password: "", new_password: "", confirm: "" });
      toast.success("Password changed");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { company_name, email, phone, website, vat_number, company_number, cis_utr, address } = branding;
      const { data } = await api.put("/branding", { company_name, email, phone, website, vat_number, company_number, cis_utr, address });
      setBranding((b) => ({ ...b, ...data }));
      toast.success("Branding saved — it'll appear on your PDFs");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBranding((b) => ({ ...b, logo_storage_path: data.logo_storage_path }));
      toast.success("Logo uploaded");
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <AppShell>
      <div className="border-b border-slate-200 bg-white px-6 md:px-8 py-5">
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Building2 className="h-6 w-6 text-blue-600" /> Settings
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Manage your account and the branding that appears on your PDFs.</p>
      </div>

      <div className="p-6 md:p-8 max-w-3xl space-y-6" data-testid="settings-page">
        {/* Team & seats */}
        <TeamSettings />

        {/* Card payments (owner) */}
        {(user?.role === "owner" || user?.is_admin) && <CardPayments />}

        {/* Xero accounting (owner/admin) */}
        {(user?.role === "owner" || user?.role === "admin" || user?.is_admin) && <XeroConnect />}

        {/* AI usage */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="ai-usage-section">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-600" /> AI usage this month</h2>
          <p className="text-sm text-slate-500 mt-0.5 mb-4">AI-powered takeoff and estimating actions you've used this month.</p>
          <div className="grid grid-cols-3 gap-4">
            <div className="rounded-lg border border-slate-200 p-4 text-center" data-testid="ai-usage-extract">
              <p className="font-display text-2xl font-bold text-navy">{aiUsage?.this_month?.extract ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">Quantity extractions</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-4 text-center" data-testid="ai-usage-scale">
              <p className="font-display text-2xl font-bold text-navy">{aiUsage?.this_month?.scale ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">Scale detections</p>
            </div>
            <div className="rounded-lg border border-slate-200 p-4 text-center" data-testid="ai-usage-rates">
              <p className="font-display text-2xl font-bold text-navy">{aiUsage?.this_month?.rates ?? 0}</p>
              <p className="text-xs text-slate-500 mt-1">Rate suggestions</p>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">{aiUsage?.this_month?.total ?? 0} AI actions this month · {aiUsage?.all_time_total ?? 0} all-time</p>
        </div>

        {/* Account */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="account-section">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><User className="h-5 w-5 text-slate-400" /> Your account</h2>
          <div className="grid sm:grid-cols-2 gap-4 mt-4">
            <div>
              <Label>Your name</Label>
              <Input value={profile.name} onChange={(e) => setProfile((p) => ({ ...p, name: e.target.value }))} data-testid="profile-name" className="mt-1" />
            </div>
            <div>
              <Label>Company / workspace</Label>
              <Input value={profile.company} onChange={(e) => setProfile((p) => ({ ...p, company: e.target.value }))} data-testid="profile-company" className="mt-1" />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={user?.email || ""} disabled className="mt-1 bg-slate-50 text-slate-400" data-testid="profile-email" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={saveProfile} disabled={savingProfile} data-testid="save-profile-btn" className="bg-slate-900 hover:bg-slate-800">
              {savingProfile ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save profile
            </Button>
          </div>
        </div>

        {/* Password */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="password-section">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><KeyRound className="h-5 w-5 text-slate-400" /> Change password</h2>
          <div className="grid sm:grid-cols-3 gap-4 mt-4">
            <div>
              <Label>Current password</Label>
              <Input type="password" value={pw.current_password} onChange={(e) => setPw((p) => ({ ...p, current_password: e.target.value }))} data-testid="current-password" className="mt-1" />
            </div>
            <div>
              <Label>New password</Label>
              <Input type="password" value={pw.new_password} onChange={(e) => setPw((p) => ({ ...p, new_password: e.target.value }))} data-testid="new-password" className="mt-1" />
            </div>
            <div>
              <Label>Confirm new</Label>
              <Input type="password" value={pw.confirm} onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))} data-testid="confirm-password" className="mt-1" />
            </div>
          </div>
          <div className="mt-4 flex justify-end">
            <Button onClick={changePassword} disabled={savingPw || !pw.current_password || !pw.new_password} data-testid="change-password-btn" className="bg-slate-900 hover:bg-slate-800">
              {savingPw ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Update password
            </Button>
          </div>
        </div>

        {/* Branding */}
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Building2 className="h-5 w-5 text-slate-400" /> Company branding</h2>
          <p className="text-sm text-slate-500 mt-0.5 mb-4">Your logo and details appear on your estimate and Application-for-Payment PDFs.</p>
          <p className="text-sm font-medium text-slate-700 mb-2">Logo</p>
          <div className="flex items-center gap-5">
            <div className="h-24 w-24 rounded-lg border border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden shrink-0" data-testid="logo-preview">
              {branding.logo_storage_path ? (
                <img src={fileUrl(branding.logo_storage_path)} alt="Logo" className="h-full w-full object-contain p-1" />
              ) : (
                <ImageIcon className="h-8 w-8 text-slate-300" />
              )}
            </div>
            <div>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg" onChange={onLogo} className="hidden" data-testid="logo-input" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-logo-btn" className="border-slate-300">
                {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />} Upload logo
              </Button>
              <p className="text-xs text-slate-400 mt-2">PNG or JPG, up to 3MB. A transparent PNG looks best.</p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            {FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input value={branding[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`branding-${f.key}`} className="mt-1" />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Label>Address</Label>
            <Textarea value={branding.address || ""} onChange={(e) => set("address", e.target.value)} rows={2} placeholder="Company address" data-testid="branding-address" className="mt-1 resize-none" />
          </div>
          <div className="mt-5 flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-branding-btn" className="bg-blue-600 hover:bg-blue-700">
              {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save branding
            </Button>
          </div>
        </div>

        {/* Payment details (BACS) */}
        <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="payment-settings-section">
          <h2 className="font-display text-lg font-bold text-slate-900 flex items-center gap-2"><Landmark className="h-5 w-5 text-slate-400" /> Payment details</h2>
          <p className="text-sm text-slate-500 mt-0.5 mb-4">Your bank details for BACS transfers. These appear as "How to pay" on the Application for Payment your clients see and on the PDF.</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {PAYMENT_FIELDS.map((f) => (
              <div key={f.key}>
                <Label>{f.label}</Label>
                <Input value={payment[f.key] || ""} onChange={(e) => setPay(f.key, e.target.value)} placeholder={f.placeholder} data-testid={`payment-${f.key}`} className="mt-1" />
              </div>
            ))}
          </div>
          <div className="mt-4">
            <Label>Payment terms / note</Label>
            <Textarea value={payment.payment_terms || ""} onChange={(e) => setPay("payment_terms", e.target.value)} rows={2} placeholder="e.g. Payment due within 30 days of application date." data-testid="payment-terms" className="mt-1 resize-none" />
          </div>
          <div className="mt-5 flex justify-end">
            <Button onClick={savePayment} disabled={savingPayment} data-testid="save-payment-btn" className="bg-blue-600 hover:bg-blue-700">
              {savingPayment ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null} Save payment details
            </Button>
          </div>
        </div>

        {/* Activity log (owner/admin) */}
        {(user?.role === "owner" || user?.role === "admin" || user?.is_admin) && <ActivityLog />}
      </div>
    </AppShell>
  );
};

export default Settings;
